import React, { useState, useEffect, useCallback } from "react";
import axios from "../../api/axiosAuth";
import { useAuth } from "../../context/AuthContext";
import { API as BASE_API } from "../config"; // ✅ Uso de configuración centralizada
import "../../style/GestionDocumentos.css";

const API_DOCS = `${BASE_API}/documentos`;

export default function GestionDocumentos() {
  const { token } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [file, setFile] = useState(null);
  const [descripcion, setDescripcion] = useState("");
  const [loading, setLoading] = useState(false);

  // Estados para filtros
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState(""); 
  
  // Estados para subida
  const [tipoDocumento, setTipoDocumento] = useState("boletin");

  /* ===============================
      🔄 CARGA DE DOCUMENTOS
  =============================== */
  const cargarDocumentos = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(API_DOCS, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          numero_documento: numeroDocumento || undefined,
          tipo_documento: tipoFiltro || undefined,
        },
      });
      setDocumentos(res.data || []);
    } catch (err) {
      console.error("❌ Error cargando documentos:", err);
    } finally {
      setLoading(false);
    }
  }, [token, numeroDocumento, tipoFiltro]);

  useEffect(() => {
    cargarDocumentos();
  }, [cargarDocumentos]);

  /* ===============================
      📤 SUBIR DOCUMENTO
  =============================== */
  const subirDocumento = async (e) => {
    e.preventDefault();
    if (!file) return alert("⚠️ Selecciona un archivo PDF o imagen");

    const formData = new FormData();
    formData.append("documento", file);
    formData.append("tipo_documento", tipoDocumento);
    formData.append("descripcion", descripcion);

    try {
      await axios.post(`${API_DOCS}/subir`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`, 
          "Content-Type": "multipart/form-data" 
        },
      });
      
      alert("✅ Documento almacenado correctamente");
      setFile(null);
      setDescripcion("");
      // Resetear el input file manualmente si es necesario
      document.getElementById("fileInput").value = "";
      cargarDocumentos();
    } catch (err) {
      console.error("❌ Error subiendo documento:", err);
      alert(err.response?.data?.message || "Error al subir el archivo");
    }
  };

  /* ===============================
      📥 DESCARGAR DOCUMENTO
  =============================== */
  const descargarDocumento = async (id, nombreOriginal) => {
    try {
      // Usamos axios para descargar como blob y manejar el token Bearer
      const response = await axios.get(`${API_DOCS}/descargar/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob', 
      });

      // Crear un link temporal para la descarga
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreOriginal || `documento_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("❌ Error en descarga:", err);
      alert("No se pudo descargar el archivo.");
    }
  };

  return (
    <div className="gestion-docs-container p-6">
      <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
        📂 Gestión de Documentos Académicos
      </h2>

      {/* SECCIÓN: FILTROS Y BÚSQUEDA */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-wrap gap-4 items-end">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-gray-500">BUSCAR POR DOCUMENTO ID</label>
          <input
            type="text"
            placeholder="Ej: 1067..."
            value={numeroDocumento}
            onChange={(e) => setNumeroDocumento(e.target.value)}
            className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-gray-500">TIPO DE ARCHIVO</label>
          <select 
            value={tipoFiltro} 
            onChange={(e) => setTipoFiltro(e.target.value)} 
            className="p-2 border rounded-lg outline-none"
          >
            <option value="">Todos los tipos</option>
            <option value="boletin">Boletín</option>
            <option value="certificado">Certificado</option>
            <option value="autorizacion">Autorización</option>
            <option value="otro">Otro</option>
          </select>
        </div>
        <button 
          onClick={cargarDocumentos}
          className="bg-gray-800 text-white px-6 py-2 rounded-lg hover:bg-black transition"
        >
          Filtrar
        </button>
      </div>

      {/* SECCIÓN: SUBIDA DE ARCHIVOS */}
      <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 mb-8">
        <h3 className="text-sm font-bold text-blue-800 mb-4 uppercase tracking-wider">Subir nuevo soporte</h3>
        <form onSubmit={subirDocumento} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <input 
            id="fileInput"
            type="file" 
            onChange={(e) => setFile(e.target.files[0])} 
            className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700"
          />
          <select 
            value={tipoDocumento} 
            onChange={(e) => setTipoDocumento(e.target.value)}
            className="p-2 border rounded-lg bg-white"
          >
            <option value="boletin">Boletín</option>
            <option value="certificado">Certificado</option>
            <option value="autorizacion">Autorización</option>
            <option value="otro">Otro</option>
          </select>
          <input
            type="text"
            placeholder="Descripción corta..."
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="p-2 border rounded-lg bg-white"
          />
          <button 
            type="submit"
            className="bg-green-600 text-white p-2 rounded-lg font-bold hover:bg-green-700 transition shadow-sm"
          >
            Subir Archivo
          </button>
        </form>
      </div>

      {/* TABLA DE RESULTADOS */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
        <table className="w-full text-left">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-4 text-xs font-bold text-gray-500 uppercase">Tipo</th>
              <th className="p-4 text-xs font-bold text-gray-500 uppercase">Nombre del Archivo</th>
              <th className="p-4 text-xs font-bold text-gray-500 uppercase">Descripción</th>
              <th className="p-4 text-xs font-bold text-gray-500 uppercase">Fecha</th>
              <th className="p-4 text-xs font-bold text-gray-500 uppercase text-center">Acción</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan="5" className="p-10 text-center text-gray-400">Cargando documentos...</td></tr>
            ) : documentos.length === 0 ? (
              <tr><td colSpan="5" className="p-10 text-center text-gray-400">No se encontraron documentos registrados.</td></tr>
            ) : (
              documentos.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                  <td className="p-4">
                    <span className="px-2 py-1 bg-gray-200 rounded text-xs font-bold uppercase text-gray-700">
                      {doc.tipo_documento}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-gray-600">{doc.nombre_archivo}</td>
                  <td className="p-4 text-sm text-gray-600 italic">{doc.descripcion || "Sin descripción"}</td>
                  <td className="p-4 text-sm text-gray-500">
                    {new Date(doc.fecha_creacion).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-center">
                    <button 
                      onClick={() => descargarDocumento(doc.id, doc.nombre_archivo)}
                      className="text-blue-600 font-bold hover:underline flex items-center justify-center gap-1 mx-auto"
                    >
                      ⬇️ Descargar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}